import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  CheckCircleIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import managementService from '../api/managementService';
import type { Asset } from '../context/ManagementContext';
import AdvancedEntitySelector from './AdvancedEntitySelector';
import { useDebounce } from '../hooks/useDebounce';

interface AssetDeviceManagementProps {
  asset: Asset;
  onClose: () => void;
}

const AssetDeviceManagement: React.FC<AssetDeviceManagementProps> = ({ asset, onClose }) => {
  // const { } = useTranslation();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [isAddDevicesOpen, setIsAddDevicesOpen] = useState(false);
  const [availableDevicesSearch, setAvailableDevicesSearch] = useState('');
  const [selectedCustomerFilter, setSelectedCustomerFilter] = useState<any>(null);
  const [isCustomerFilterOpen, setIsCustomerFilterOpen] = useState(false);
  const [selectedAvailableDevices, setSelectedAvailableDevices] = useState<string[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const debouncedAvailableSearch = useDebounce(availableDevicesSearch, 300);

  // Get current asset devices
  const { data: assetDevices = [], isLoading: isLoadingDevices } = useQuery({
    queryKey: ['asset-devices', asset.id],
    queryFn: () => managementService.asset.getDevices(asset.id),
  });

  // Store devices from the available devices query for pending display
  const [deviceCache, setDeviceCache] = useState<Record<string, any>>({});

  // Removed unused customers query

  // Initialize selected devices when asset devices are loaded
  React.useEffect(() => {
    if (assetDevices.length > 0 && selectedDevices.length === 0) {
      setSelectedDevices(assetDevices.map(d => d.id));
    }
  }, [assetDevices, selectedDevices.length]);

  // Track changes
  React.useEffect(() => {
    const currentDeviceIds = assetDevices.map(d => d.id).sort();
    const selectedDeviceIds = selectedDevices.slice().sort();
    const hasChanges = JSON.stringify(currentDeviceIds) !== JSON.stringify(selectedDeviceIds);
    setHasUnsavedChanges(hasChanges);
  }, [assetDevices, selectedDevices]);

  // Get available devices (not assigned to this asset)
  const { data: availableDevicesData, isLoading: isLoadingAvailable } = useQuery({
    queryKey: ['available-devices', debouncedAvailableSearch, selectedCustomerFilter?.id, asset.id],
    queryFn: () => managementService.device.getAll({
      search: debouncedAvailableSearch,
      customerId: selectedCustomerFilter?.id,
      limit: 50,
    }),
    enabled: isAddDevicesOpen,
  });

  // Update device cache when available devices data changes
  React.useEffect(() => {
    if (availableDevicesData?.data) {
      const newCache = { ...deviceCache };
      availableDevicesData.data.forEach(device => {
        newCache[device.id] = device;
      });
      setDeviceCache(newCache);
    }
  }, [availableDevicesData]);

  // Mutations
  const saveChangesMutation = useMutation({
    mutationFn: async () => {
      // Get current device IDs
      const currentDeviceIds = assetDevices.map(d => d.id);
      
      // Calculate devices to remove (currently associated but not selected)
      const devicesToRemove = currentDeviceIds.filter(id => !selectedDevices.includes(id));
      
      // Calculate devices to add (selected but not currently associated)
      const devicesToAdd = selectedDevices.filter(id => !currentDeviceIds.includes(id));
      
      // Remove devices first
      if (devicesToRemove.length > 0) {
        await managementService.asset.disassociateDevices(asset.id, devicesToRemove);
      }
      
      // Then add new devices
      if (devicesToAdd.length > 0) {
        await managementService.asset.associateDevices(asset.id, devicesToAdd);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-devices', asset.id] });
      queryClient.invalidateQueries({ queryKey: ['available-devices'] });
      setHasUnsavedChanges(false);
    },
  });

  // Removed unused associateDevicesMutation as it's not being used

  // Create combined list of devices to display (both stored and pending)
  const displayDevices = React.useMemo(() => {
    // Get devices that are selected (including pending ones)
    const selectedDeviceObjects = selectedDevices.map(deviceId => {
      // First try to find in current asset devices
      let device = assetDevices.find(d => d.id === deviceId);
      
      // If not found, look in device cache
      if (!device) {
        device = deviceCache[deviceId];
      }
      
      return device;
    }).filter(Boolean); // Remove any undefined devices
    
    return selectedDeviceObjects;
  }, [selectedDevices, assetDevices, deviceCache]);

  // Filter display devices by search term
  const filteredDevices = displayDevices.filter(device => {
    if (!device) return false;
    return (
      device.name?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      device.description?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      device.gatewayId?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      device.slaveId?.toString().includes(debouncedSearchTerm)
    );
  });

  const handleSaveChanges = () => {
    saveChangesMutation.mutate();
  };

  const handleAssociateDevices = () => {
    if (selectedAvailableDevices.length > 0) {
      // Add selected devices to the main selection
      const newSelection = [...new Set([...selectedDevices, ...selectedAvailableDevices])];
      setSelectedDevices(newSelection);
      setSelectedAvailableDevices([]);
      setIsAddDevicesOpen(false);
    }
  };

  // Removed unused deviceColumns as it's not being used

  return (
    <Dialog open={true} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
      <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
        <DialogPanel className="max-w-4xl w-full bg-white rounded-lg shadow-xl max-h-[80vh] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Manage Devices - {asset.label}
                {hasUnsavedChanges && (
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    Unsaved changes
                  </span>
                )}
              </DialogTitle>
              <p className="text-sm text-gray-500 mt-1">
                Select/deselect devices and click "Save Changes" to apply
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Current Devices Section */}
            <div className="p-6 flex-1 overflow-hidden flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-md font-medium text-gray-900">
                  Selected Devices ({selectedDevices.length})
                  {assetDevices.length !== selectedDevices.length && (
                    <span className="text-sm text-gray-500 ml-2">
                      ({assetDevices.length} saved)
                    </span>
                  )}
                </h3>
                <div className="flex items-center space-x-2">
                  {hasUnsavedChanges && (
                    <button
                      onClick={handleSaveChanges}
                      disabled={saveChangesMutation.isPending}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                    >
                      <CheckCircleIcon className="h-4 w-4 mr-2" />
                      {saveChangesMutation.isPending ? 'Saving...' : 'Save Changes'}
                    </button>
                  )}
                  <button
                    onClick={() => setIsAddDevicesOpen(true)}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Add Devices
                  </button>
                </div>
              </div>

              {/* Search */}
              <div className="relative mb-4">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search devices..."
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Devices Table */}
              <div className="flex-1 overflow-auto">
                {isLoadingDevices ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="text-gray-500">Loading devices...</div>
                  </div>
                ) : selectedDevices.length === 0 ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="text-gray-500">No devices selected</div>
                  </div>
                ) : filteredDevices.length === 0 ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="text-gray-500">No devices match your search</div>
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={selectedDevices.length === filteredDevices.length && filteredDevices.length > 0}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedDevices(filteredDevices.filter(d => d != null).map(d => d.id));
                              } else {
                                setSelectedDevices([]);
                              }
                            }}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Gateway
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Slave ID
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Customer
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Current Asset
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredDevices.filter(device => device != null).map((device) => (
                        <tr key={device.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedDevices.includes(device.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedDevices([...selectedDevices, device.id]);
                                } else {
                                  setSelectedDevices(selectedDevices.filter(id => id !== device.id));
                                }
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            <div className="flex items-center">
                              {device.name || `Device ${device.slaveId}`}
                              {!assetDevices.find(d => d.id === device.id) && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  Pending
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {device.gatewayId}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {device.slaveId}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {device.description || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </DialogPanel>
      </div>

      {/* Add Devices Modal */}
      {isAddDevicesOpen && (
        <Dialog open={isAddDevicesOpen} onClose={() => setIsAddDevicesOpen(false)} className="relative z-50">
          <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
          <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
            <DialogPanel className="max-w-2xl w-full bg-white rounded-lg shadow-xl max-h-[70vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <DialogTitle className="text-lg font-semibold text-gray-900">
                  Add Devices to Asset
                </DialogTitle>
                <button
                  onClick={() => setIsAddDevicesOpen(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="flex-1 overflow-hidden flex flex-col p-6">
                {/* Search and Filter */}
                <div className="space-y-4 mb-4">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search available devices..."
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={availableDevicesSearch}
                      onChange={(e) => setAvailableDevicesSearch(e.target.value)}
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-700">Customer:</label>
                    <button
                      type="button"
                      onClick={() => setIsCustomerFilterOpen(true)}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      {selectedCustomerFilter ? (
                        <>
                          <BuildingOfficeIcon className="h-4 w-4 mr-2 text-blue-600" />
                          {selectedCustomerFilter.name}
                        </>
                      ) : (
                        <>
                          <BuildingOfficeIcon className="h-4 w-4 mr-2 text-gray-400" />
                          All Customers
                        </>
                      )}
                    </button>
                    {selectedCustomerFilter && (
                      <button
                        type="button"
                        onClick={() => setSelectedCustomerFilter(null)}
                        className="inline-flex items-center p-1 rounded-full text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        title="Clear customer filter"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Available Devices Table */}
                <div className="flex-1 overflow-auto">
                  {isLoadingAvailable ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="text-gray-500">Loading available devices...</div>
                    </div>
                  ) : (!availableDevicesData?.data || availableDevicesData.data.length === 0) ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="text-gray-500">No available devices found</div>
                    </div>
                  ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left">
                            <input
                              type="checkbox"
                              checked={selectedAvailableDevices.length === availableDevicesData.data.length && availableDevicesData.data.length > 0}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedAvailableDevices(availableDevicesData.data.map(d => d.id));
                                } else {
                                  setSelectedAvailableDevices([]);
                                }
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Gateway
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Slave ID
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Customer
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Current Asset
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Description
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {availableDevicesData.data.map((device) => (
                          <tr key={device.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={selectedAvailableDevices.includes(device.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedAvailableDevices([...selectedAvailableDevices, device.id]);
                                  } else {
                                    setSelectedAvailableDevices(selectedAvailableDevices.filter(id => id !== device.id));
                                  }
                                }}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {device.name || `Device ${device.slaveId}`}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {device.gatewayId}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {device.slaveId}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {device.customer?.name || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {device.asset?.label || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {device.description || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
                <button
                  onClick={() => setIsAddDevicesOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssociateDevices}
                  disabled={selectedAvailableDevices.length === 0}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                  {`Add to Selection (${selectedAvailableDevices.length})`}
                </button>
              </div>
            </DialogPanel>
          </div>
        </Dialog>
      )}

      {/* Customer Filter Selector */}
      {isCustomerFilterOpen && (
        <div className="relative z-[60]">
          <AdvancedEntitySelector
            isOpen={isCustomerFilterOpen}
            onClose={() => setIsCustomerFilterOpen(false)}
            onSelect={(entity) => {
              setSelectedCustomerFilter(entity);
            }}
            entityType="customer"
            title="Filter by Customer"
            selectedId={selectedCustomerFilter?.id}
          />
        </div>
      )}
    </Dialog>
  );
};

export default AssetDeviceManagement;
